import {
  Component,
  effect,
  ElementRef,
  inject,
  input,
  OnDestroy,
  OnInit,
  output,
  signal,
  untracked,
} from '@angular/core';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';

import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { TranslateModule } from '@ngx-translate/core';
import { CmUiChipComponent, CmUiEmptyStateComponent, UiLoaderComponent } from '@cm/ui-modules';
import { CampaignRestService, CampaignSearchItem } from '@shared/services/campaign-rest.service';

let nextPanelId = 0;

@Component({
  selector: 'cm-ui-button-campaign-dropdown',
  standalone: true,
  imports: [MatFormFieldModule, MatIconModule, MatInputModule, TranslateModule, CmUiChipComponent, CmUiEmptyStateComponent, UiLoaderComponent],
  templateUrl: './campaign-dropdown.component.html',
  styleUrls: ['./campaign-dropdown.component.scss'],
  host: { '(document:click)': 'onDocumentClick($event)', '(document:keydown.escape)': 'closePanel()' },
})
export class CampaignDropdownComponent implements OnInit, OnDestroy {
  placeholder = input<string>('ADMIN.campaignDropdown.placeholder');
  /** The campaign to show as picked — lets a page restore a persisted choice (RULE 2). */
  value = input<CampaignSearchItem | null>(null);

  campaignChange = output<CampaignSearchItem | null>();

  /** Id of the panel the field controls (aria-controls) — unique per instance. */
  readonly panelId = `campaign-dropdown-panel-${nextPanelId++}`;

  isOpen = signal(false);
  loading = signal(false);
  items = signal<CampaignSearchItem[]>([]);
  selected = signal<CampaignSearchItem | null>(null);
  searchTerm = signal('');

  private readonly campaignService = inject(CampaignRestService);
  private readonly elementRef = inject(ElementRef);
  private readonly search$ = new Subject<string>();
  private sub = new Subscription();

  constructor() {
    effect(() => {
      const value = this.value();
      untracked(() => this.selected.set(value));
    });
  }

  ngOnInit(): void {
    this.sub.add(
      this.search$
        .pipe(
          debounceTime(300),
          distinctUntilChanged(),
          switchMap((term) => {
            this.loading.set(true);
            return this.campaignService.search(term);
          }),
        )
        .subscribe({
          next: (results) => {
            this.items.set(results);
            this.loading.set(false);
          },
          error: () => {
            this.loading.set(false);
          },
        }),
    );
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }

  togglePanel(event: Event): void {
    event.stopPropagation();
    const opening = !this.isOpen();
    this.isOpen.set(opening);
    if (opening) {
      this.searchTerm.set('');
      this.search$.next('');
    }
  }

  onSearchInput(value: string): void {
    this.searchTerm.set(value);
    this.search$.next(value);
  }

  select(item: CampaignSearchItem): void {
    this.selected.set(item);
    this.isOpen.set(false);
    this.campaignChange.emit(item);
  }

  clear(): void {
    this.selected.set(null);
    this.campaignChange.emit(null);
  }

  /** Enter / Space on the field itself (not on the chip's × inside it) opens or closes the panel. */
  onTriggerKey(event: Event): void {
    if (event.target !== event.currentTarget) return;
    event.preventDefault();
    this.togglePanel(event);
  }

  closePanel(): void {
    this.isOpen.set(false);
  }

  onDocumentClick(event: Event): void {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.isOpen.set(false);
    }
  }
}
